/**
 * Connect-level auth. One source of truth for the OAuth bearer token, the only
 * credential the backend accepts. Mirrors it into the one legacy per-app key a
 * sub-app still reads directly (`nx_token`).
 */

import { ref, type Ref } from 'vue';
import {
  invalidateAccountSelection,
  invalidateAccountSelectionForTenant,
  refreshAccountSelection,
} from '@blpde/connect/auth';
import {
  API_BASE_CHANGED_EVENT,
  buildAuthHeaders,
  getApiOrigin,
  getDeploymentApiOrigin,
  SESSION_ENDING_EVENT,
  hasAuthCredentials,
  readBearer,
  type ShellImpersonationStart,
} from '@blpde/connect';
import { localeHeader } from '@blpde/connect/http';
import { loadShellConfigs } from '@/settings';
import { registerBrowserDevice } from '@/browserDevice';
import type { SupportLink } from '@/supportLink';

// The read + fetch side now lives in @blpde/connect (single source of truth);
// re-export it so existing `@/auth` import sites keep working. The shell still
// owns login/refresh/token-writes below.
export { buildAuthHeaders, getApiOrigin, hasAuthCredentials, readBearer };

// The one remaining per-app mirror: erp's api/api.ts reads `nx_token` directly
// (TOKEN_KEY — own auth store + SSO handoff). Every other sub-app reads the
// shared `blp_token` only.
const MIRRORED_SUBAPPS = ['nx'] as const;

// Keys older builds mirrored the bearer and the session UUID into. No longer
// written or read — cleared on logout so stale credentials from previous builds
// can't linger (a surviving stale `forge_token` once caused 401/reload loops).
// Includes renamed-away apps (metis → agent+flow, pulse → chat) so logout
// still wipes stale <app>_token / <app>_session keys from older builds.
const LEGACY_SUBAPP_KEYS = [
  'forge',
  'agent',
  'flow',
  'metis',
  'pulse',
  'chat',
  'mail',
  'calendar',
  'calc',
  'drive',
  'designer',
  'wiki',
  'write',
] as const;

const API_BASE_STORAGE_KEY = 'formica.api.baseUrl';
const DEFAULT_API_BASE = 'http://localhost:7680';
const LAST_EMAIL_STORAGE_KEY = 'formica.connect.lastEmail';

// The shell's OAuth client. Master seeds it as a PUBLIC client, so it carries
// no secret and `client_id` in the request body IS the client authentication
// the token endpoint expects (RFC 6749 §2.1).
const OAUTH_CLIENT_ID = 'universal';

// Deadline for the identity round trip. The router awaits it before the first
// authenticated view mounts, so it doubles as the shell's boot budget: long
// enough for a slow link, short enough that a wrong host lets the user back to
// the login form to fix it.
const USERINFO_TIMEOUT_MS = 5_000;

// Shared OAuth-session storage used by every PKCE-style sub-app
// (mail/calendar/drive/calc). Their pinia auth stores treat the absence of
// these keys as "logged out", even when the simple `<app>_token` is set —
// so Connect must mirror its session into them too.
const SHARED_OAUTH_SESSION_KEY = 'formica.oauth.session';
const SHARED_OAUTH_ACCOUNTS_KEY = 'formica.oauth.accounts';
const SHARED_OAUTH_ACTIVE_KEY = 'formica.oauth.activeAccountId';

// The impersonator's own credentials while an impersonation session runs
// (I-2327): their bearer, refresh token and session blob, plus the admin route
// to return to. Present exactly while this browser is impersonating.
export const IMPERSONATION_STORAGE_KEY = 'formica.oauth.impersonation';

/**
 * A refused `/oauth/login`. `code` is master's `{"error": …}` (empty when the
 * body names none), `status` the HTTP status. The message is the code, so a
 * caller that only reads `message` still gets it.
 */
export class LoginError extends Error {
  readonly code: string;
  readonly status: number;

  constructor(code: string, status: number) {
    super(code || `login failed (${status})`);
    this.name = 'LoginError';
    this.code = code;
    this.status = status;
  }
}

/**
 * The account has to set a new password before it may sign in (I-2301):
 * master answers the right password with 403 and this code. Repeating the same
 * login with `newPassword` sets it and signs in.
 */
export const PASSWORD_CHANGE_REQUIRED = 'password_change_required';

/** Whether a failed `login()` asks for a new password rather than failing. */
export function isPasswordChangeRequired(err: unknown): err is LoginError {
  return err instanceof LoginError && err.code === PASSWORD_CHANGE_REQUIRED;
}

export interface LoginOptions {
  /**
   * The new password of an account that has to change its password — only
   * after a login answered {@link PASSWORD_CHANGE_REQUIRED}. Sent with the same
   * email and current password; master checks it (400 `missing_field`,
   * `weak_password`, `unchanged_password`), stores it and signs in. Ignored by
   * master for any other account. Never stored on the client.
   */
  newPassword?: string;
  /**
   * The company the sign-in page was opened for (the central login sends a
   * person on to the hostname of their company, edge rebuild plan W18). A hint,
   * never a grant: master preselects that company after the sign-in when the
   * account belongs to it and ignores it otherwise. Only a well-formed uuid.
   */
  companyHint?: string;
}

export interface LoginResult {
  accessToken: string;
  refreshToken?: string;
  account?: { id: number; email?: string; name?: string } | null;
  user?: { id: number; email?: string; name?: string } | null;
}

/** Normalizes user input — accepts `host`, `host:port`, `http://host:port`, …
 *  Stores just the origin so callers can append `/v1/...` predictably. */
export function normalizeApiBase(value: string): string {
  const trimmed = (value || '').trim();
  if (!trimmed) return DEFAULT_API_BASE;
  const hasProto = /^https?:\/\//i.test(trimmed);
  try {
    const url = new URL(hasProto ? trimmed : `http://${trimmed}`);
    return `${url.protocol}//${url.host}`;
  } catch {
    return DEFAULT_API_BASE;
  }
}

export function saveApiBase(value: string): string {
  const norm = normalizeApiBase(value);
  const previous = getApiOrigin();
  localStorage.setItem(API_BASE_STORAGE_KEY, norm);
  // The shared mux socket closes its connection to the old server on this,
  // so nothing keeps talking to it (or hands it the next token).
  if (getApiOrigin() !== previous && typeof window !== 'undefined') {
    window.dispatchEvent(new Event(API_BASE_CHANGED_EVENT));
  }
  return norm;
}

/** Takes `?url=` (the API origin override) out of the address bar, keeping the rest. */
function dropApiUrlOverride(): void {
  if (typeof window === 'undefined') return;
  const { pathname = '/', search = '', hash = '' } = window.location;
  const query = new URLSearchParams(search);
  if (!query.has('url')) return;
  query.delete('url');
  const text = query.toString();
  window.history.replaceState(window.history.state, '', `${pathname}${text ? `?${text}` : ''}${hash}`);
}

function apiBase(origin: string = getApiOrigin()): string {
  return `${origin}/v1`;
}

/** What differs between the sign-ins that share {@link signInAt}. */
interface SignInOptions {
  /** `false`: this browser is not registered as a device of the account. */
  device?: boolean;
  /** `false`: the account's address is not offered on the next sign-in form. */
  rememberEmail?: boolean;
  /**
   * The sign-in replaces the session this browser has, completely: the hook
   * for the session it replaces runs (`account_switch`), then every credential
   * and cached identity of it is cleared — its refresh token above all, which
   * would restore it (or send its credential to another installation) after the
   * new session's first 401. Only once the server accepted the sign-in: a refused
   * one leaves the old session alone.
   */
  replaceSession?: boolean;
}

/**
 * The generation of the session this browser holds. Every operation that
 * writes credentials from a server answer (a refresh, a sign-in) notes the
 * generation it started under and drops its answer when the session was
 * replaced meanwhile — a sign-out, a sign-in, an impersonation starting or
 * ending, a support link redeemed. Without it a refresh begun before the
 * replacement writes the previous account's bearer and refresh token back over
 * the new session.
 */
let _sessionGeneration = 0;

/** The login marker the user state of this tab was loaded for (undefined: not loaded yet). */
let _stateMarker: string | null | undefined;

function replaceSessionGeneration(): void {
  _sessionGeneration += 1;
}

// --- The login marker ---------------------------------------------------------
//
// `window.formica.loginId()`: an opaque id of the current login for apps that
// bind something to it (the edge app's remote support secret). Random, never
// the token or derived from it; new with every sign-in (password, login link,
// support link, an impersonation starting or ending), the same across token
// refreshes and reloads of that login (kept next to the credentials), gone
// when nobody is signed in.
const LOGIN_ID_STORAGE_KEY = 'formica.oauth.loginId';

function randomLoginId(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

/** A new login begins: the session is replaced and gets a new marker. */
function startLogin(): void {
  replaceSessionGeneration();
  safeSet(LOGIN_ID_STORAGE_KEY, randomLoginId());
  safeRemove(SUPPORT_STORAGE_KEY);
}

/**
 * The support metadata a redemption answered (`support`: operator, request,
 * end), kept next to the credentials: if the first `/oauth/userinfo` after the
 * reload fails (timeout, 503), the banner and the end timer still have it.
 * Userinfo's own answer replaces it.
 */
const SUPPORT_STORAGE_KEY = 'formica.oauth.support';

function readStoredSupport(): ShellSupport | null {
  const raw = readRaw(SUPPORT_STORAGE_KEY);
  if (!raw) return null;
  try {
    return parseSupport({ support: JSON.parse(raw) });
  } catch {
    return null;
  }
}

/**
 * Adopts a bearer captured from the address (`?token=`, the master-gateway
 * redirect). It is a new session, so it goes through the same replacement as a
 * sign-in: the before-sign-out hook (`account_switch`) for the session it
 * replaces, the previous auth state cleared (refresh token, session blob,
 * support metadata, login marker) and a new marker. Without this the new
 * account would inherit the old principal and marker — and could open the old
 * account's approved support link under the old audit identity. Returns false
 * (and changes nothing) for the token the browser already holds.
 */
export function adoptToken(token: string): boolean {
  if (!token || readRaw('blp_token') === token) return false;
  if (hasAuthCredentials()) {
    runBeforeSignOut('account_switch');
    // Defense: session-bound resources (the mux binding) end with the session.
    window.dispatchEvent(new Event(SESSION_ENDING_EVENT));
  }
  clearAuth();
  safeSet('blp_token', token);
  safeSet('nx_token', token);
  startLogin();
  resetStoredSessionValidation();
  return true;
}

/**
 * A full reload of the shell into `url` (`location.replace`): the way out of a
 * session replacement that happened in this tab without a reload of its own —
 * the mux, queued writes, open dialogs and timers of the old session do not
 * survive it.
 */
export function reloadShellInto(url: string): void {
  const next = new URL(url, window.location.href);
  const here = window.location;
  const samePage = next.origin + next.pathname + next.search === here.origin + here.pathname + here.search;
  window.location.replace(url);
  // A change of the fragment alone is no navigation: the browser would not
  // reload, and the page — its mux, timers, queues — would go on as the old
  // account. Force it.
  if (samePage) window.location.reload();
}

/**
 * A token that would replace a DIFFERENT existing session (`?token=` over
 * another account's login) is never installed into the running shell: B's
 * credentials in A's page would let A's mux, queued writes and open documents
 * act as B (and a cancelled reload prompt would leave them there). It waits in
 * the tab's session storage — with an expiry — and the page reloads; the next
 * boot adopts it before anything mounts ({@link adoptPendingToken}). If the
 * reload is cancelled nothing changed, and the entry expires unused.
 */
const PENDING_ADOPTION_KEY = 'formica.pendingAdoption';
const PENDING_ADOPTION_TTL_MS = 30_000;

export function stageTokenAdoption(token: string): void {
  loginId(); // the originating login has a marker to be bound to
  try {
    sessionStorage.setItem(
      PENDING_ADOPTION_KEY,
      JSON.stringify({
        token,
        expires: Date.now() + PENDING_ADOPTION_TTL_MS,
        // The login this was staged from: a sign-out (or any other login) in
        // between ends it — a cancelled reload must not sign in as B later.
        login: readRaw(LOGIN_ID_STORAGE_KEY),
      }),
    );
  } catch {
    /* no session storage: the token is simply not adopted */
  }
}

/**
 * Boot, before any app or the mux mounts: adopts a staged token that has not
 * expired (a new login, `adoptToken`) and deletes the entry — always, so an
 * expired or malformed one is gone with the next boot. True when it adopted.
 */
export function adoptPendingToken(): boolean {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(PENDING_ADOPTION_KEY);
    sessionStorage.removeItem(PENDING_ADOPTION_KEY);
  } catch {
    return false;
  }
  if (!raw) return false;
  try {
    const entry = JSON.parse(raw) as { token?: unknown; expires?: unknown; login?: unknown };
    if (typeof entry.token !== 'string' || !entry.token) return false;
    if (typeof entry.expires !== 'number' || entry.expires < Date.now()) return false;
    if ((entry.login ?? null) !== readRaw(LOGIN_ID_STORAGE_KEY)) return false;
    return adoptToken(entry.token);
  } catch {
    return false;
  }
}

/** The login marker as stored (no creation): null when nobody is signed in. For pages that queue work under it. */
export function loginMarker(): string | null {
  return readRaw(LOGIN_ID_STORAGE_KEY);
}

/** The marker of the current login; '' when nobody is signed in. */
export function loginId(): string {
  if (!hasAuthCredentials()) return '';
  const kept = readRaw(LOGIN_ID_STORAGE_KEY);
  if (kept) return kept;
  // A login from before the marker existed gets one now, kept from then on.
  const created = randomLoginId();
  safeSet(LOGIN_ID_STORAGE_KEY, created);
  return created;
}

/**
 * What every sign-in does around its request — the password login and the
 * mailed login link answer with the same keys (`POST /v1/oauth/login`,
 * `POST /v1/oauth/login_link`): register (or find) this browser as a device and
 * name it, post, turn a refusal into a {@link LoginError}, persist the tokens
 * and warm the shell's grants. `body` is what differs per sign-in; it is sent
 * and never kept.
 */
async function signInAt(
  path: string,
  body: Record<string, unknown>,
  origin?: string,
  options: SignInOptions = {},
): Promise<LoginResult> {
  const generation = _sessionGeneration;
  // The marker is shared with the other tabs: a tab that signed in meanwhile
  // changed it, which the per-tab generation cannot see.
  loginId();
  const marker = readRaw(LOGIN_ID_STORAGE_KEY);
  const base = apiBase(origin);
  // Register (or find) this browser as a device first and name it on the
  // login, so master binds the session to it and the account lists it under
  // System › Devices. Best-effort: without a device the login still runs.
  const deviceUUID = options.device === false ? null : await registerBrowserDevice(base);
  const res = await fetch(`${base}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...localeHeader() },
    body: JSON.stringify({
      ...body,
      clientID: OAUTH_CLIENT_ID,
      scopes: 'all',
      ...(deviceUUID ? { deviceUUID } : {}),
    }),
  });
  if (!res.ok) {
    const answer = await res.json().catch(() => ({} as Record<string, unknown>));
    const code = (answer as { error?: unknown }).error;
    throw new LoginError(typeof code === 'string' ? code : '', res.status);
  }
  const data = await res.json() as {
    access_token: string;
    refresh_token?: string;
    account?: LoginResult['account'];
    user?: LoginResult['user'];
    support?: unknown;
  };
  const result: LoginResult = {
    accessToken: data.access_token,
    refreshToken: data.refresh_token,
    account: data.account ?? null,
    user: data.user ?? null,
  };
  // The session was replaced while this sign-in was on its way: its answer
  // belongs to a session that is gone, and is never written.
  if (generation !== _sessionGeneration || readRaw(LOGIN_ID_STORAGE_KEY) !== marker) {
    throw new LoginError('session_replaced', 0);
  }
  if (options.replaceSession && hasAuthCredentials()) {
    runBeforeSignOut('account_switch');
    clearAuth();
  }
  startLogin();
  // A fresh login supersedes an impersonation this browser may still remember:
  // its saved credentials are no longer the ones to return to.
  safeRemove(IMPERSONATION_STORAGE_KEY);
  // The session belongs to the server that issued it: when the sign-in named
  // its server, that one becomes the saved one, so the API calls that follow
  // do not carry these tokens to an older saved server.
  if (origin) {
    // `?url=` outranks the saved server in getApiOrigin(), so it would keep
    // naming another server for the session persisted below and for every later
    // request: take it out of the address first. Everything is then resolved
    // from the saved server, which is the origin this sign-in went to.
    dropApiUrlOverride();
    saveApiBase(origin);
  }
  persist(result, { rememberEmail: options.rememberEmail !== false });
  if (options.replaceSession && data.support) safeSet(SUPPORT_STORAGE_KEY, JSON.stringify(data.support));
  // Warm shell app grants immediately so the first home-tile click does not
  // race the entitlement fetch and get bounced back to /.
  void import('@/applications').then((m) => {
    m.holdAccountApplications();
    return m.refreshAccountApplications();
  });
  return result;
}

/**
 * Signs in with email + password. A refusal throws a {@link LoginError};
 * `isPasswordChangeRequired(err)` tells the one refusal that asks for a new
 * password (repeat the call with `options.newPassword`).
 */
export function login(email: string, password: string, options: LoginOptions = {}): Promise<LoginResult> {
  return signInAt('/oauth/login', {
    email,
    password,
    ...(options.newPassword ? { newPassword: options.newPassword } : {}),
    ...(options.companyHint ? { companyHint: options.companyHint } : {}),
  });
}

/**
 * Signs in with the one-time token of a mailed login link (edge rebuild plan
 * W18 slice 4): the same device, client and answer as {@link login}, and a
 * refusal is a {@link LoginError} too (`invalid_login_link`, `account_inactive`,
 * 403 from the external-access record). The token is the credential of one
 * sign-in: it goes into the request body and nowhere else — no storage, no log,
 * no error message.
 */
export function loginWithLink(token: string): Promise<LoginResult> {
  // Only ever the deployment this page was served for. A saved server (left by
  // an earlier sign-in against another installation) or a `?url=` must not
  // receive the token: the link was mailed by the installation behind this page.
  return signInAt('/oauth/login_link', { token }, getDeploymentApiOrigin());
}

/**
 * Signs in with the one-time link of an approved remote support request
 * (Fernwartung, `POST /v1/oauth/support_link`): the answer is an access token
 * of the customer's admin account and no refresh token — like an impersonation
 * login, the session ends at the time the admin approved. The browser is no
 * device of that account and its address is not remembered: the browser is the
 * operator's, the account the customer's. A refusal is a {@link LoginError};
 * master does not tell an unknown, used, expired or not approved link apart.
 * The secret goes into the request body and nowhere else — no storage, no log,
 * no error message — and only to the deployment this page was served for.
 */
export function loginWithSupportLink(link: SupportLink): Promise<LoginResult> {
  return signInAt(
    '/oauth/support_link',
    { id: link.id, secret: link.secret },
    getDeploymentApiOrigin(),
    { device: false, rememberEmail: false, replaceSession: true },
  );
}

/** localStorage write that never throws — private-browsing mode and quota
 *  limits must degrade gracefully instead of hard-failing the login flow. */
function safeSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* storage unavailable / quota exceeded — ignore */
  }
}

function safeRemove(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore — see safeSet */
  }
}

export function persist(r: LoginResult, options: { rememberEmail?: boolean } = {}) {
  if (!r.accessToken) return;
  // New credentials, so the boot verdict no longer describes them. Without
  // this, any login path that does not hard-reload the page keeps hitting a
  // cached rejection and is thrown straight back to /login.
  resetStoredSessionValidation();
  safeSet('blp_token', r.accessToken);
  if (r.refreshToken) safeSet('blp_refresh_token', r.refreshToken);
  // Mirror only into the keys a sub-app still reads directly (erp/nx).
  for (const app of MIRRORED_SUBAPPS) safeSet(`${app}_token`, r.accessToken);
  // A token REFRESH (performRefresh) returns only the new token — no
  // user/account. Carry the cached identity forward so the refreshed session
  // keeps its user/account (the shell user pill) instead of going blank.
  const prev = readStoredSession();
  const user = r.user ?? (prev?.user as LoginResult['user']) ?? null;
  const account = r.account ?? (prev?.account as LoginResult['account']) ?? null;
  // One shared session blob — the single source for sub-app identity. They read
  // email/name from it via `readShellAccount()` / `readShellUser()` in
  // @blpde/connect; the bearer itself rides the flat `blp_token` keys above.
  const session: Record<string, unknown> = {
    access_token: r.accessToken,
    accessToken: r.accessToken,
    apiBaseUrl: `${getApiOrigin()}/v1`,
  };
  if (r.refreshToken) {
    session.refresh_token = r.refreshToken;
    session.refreshToken = r.refreshToken;
  }
  if (user) session.user = user;
  if (account) session.account = account;
  safeSet(SHARED_OAUTH_SESSION_KEY, JSON.stringify(session));
  const lastEmail = account?.email || user?.email || '';
  if (lastEmail && options.rememberEmail !== false) safeSet(LAST_EMAIL_STORAGE_KEY, lastEmail);
  // Same-page sub-apps cache the bearer in a store that can't observe this
  // write (the `storage` event is cross-tab only). Signal them to re-read so a
  // silent refresh propagates without a full reload.
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('formica:auth-changed'));
  }
}

/** Parsed shared OAuth session blob, or null when absent/corrupt. */
function readStoredSession(): Record<string, unknown> | null {
  try {
    const raw = localStorage.getItem(SHARED_OAUTH_SESSION_KEY);
    return raw ? (JSON.parse(raw) as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

export function clearAuth() {
  replaceSessionGeneration();
  dropPendingAdoption();
  refreshUnanswered = false;
  invalidateAccountSelection();
  const keys = [
    'blp_token',
    'blp_refresh_token',
    LOGIN_ID_STORAGE_KEY,
    SUPPORT_STORAGE_KEY,
    REFRESHED_AT_KEY,
    // Session UUID of older builds: no longer written (it never authenticates),
    // still cleared so browsers that stored one get cleaned up.
    'blp_session',
    SHARED_OAUTH_SESSION_KEY,
    // The impersonator's saved credentials: a wipe leaves nothing to return to.
    IMPERSONATION_STORAGE_KEY,
    // Legacy multi-account keys: no longer written (single-session model) but
    // still cleared so stale blobs from older builds don't linger.
    SHARED_OAUTH_ACCOUNTS_KEY,
    SHARED_OAUTH_ACTIVE_KEY,
    ...MIRRORED_SUBAPPS.flatMap((a) => [`${a}_token`, `${a}_session`]),
    ...LEGACY_SUBAPP_KEYS.flatMap((a) => [`${a}_token`, `${a}_session`]),
  ];
  for (const k of keys) safeRemove(k);
  // Note: `formica.api.baseUrl` + `formica.connect.lastEmail` stay so the
  // user doesn't re-type host/email on the next login, and
  // `formica.connect.deviceSerial` stays so the next login finds the same
  // browser device instead of registering a new one (see browserDevice.ts).
  _user.value = null;
  cancelImpersonationTimer();
  cancelSupportTimer();
  // The boot verdict described credentials that no longer exist.
  resetStoredSessionValidation();
}

export function loadLastEmail(): string {
  return localStorage.getItem(LAST_EMAIL_STORAGE_KEY) || '';
}

export function saveLastEmail(email: string): void {
  const trimmed = (email || '').trim();
  if (!trimmed) return;
  localStorage.setItem(LAST_EMAIL_STORAGE_KEY, trimmed);
}

export function isAuthenticated(): boolean {
  return hasAuthCredentials();
}

/** The account that started an impersonation session (`/oauth/userinfo`). */
export interface ShellImpersonator {
  accountId: number | null;
  accountUuid: string | null;
  name: string;
  email: string;
}

/** An impersonation session: who started it and when master ends it. */
export interface ShellImpersonation {
  impersonator: ShellImpersonator;
  /** RFC 3339; null when master named none. */
  expiresAt: string | null;
}

/**
 * A remote support session (Fernwartung): the BLP support operator works
 * as the customer's admin account until the time the admin approved.
 */
export interface ShellSupport {
  operatorName: string;
  requestId: string;
  /** RFC 3339; null when master named none. */
  endsAt: string | null;
}

export interface ShellUser {
  id: number;
  name: string;
  email: string;
  accountId: number | null;
  accountUuid: string | null;
  /** Set while the bearer belongs to an impersonation session. */
  impersonation?: ShellImpersonation | null;
  /** Set while the bearer belongs to a remote support session. */
  support?: ShellSupport | null;
  /**
   * The session may decide remote support requests (`support_approver` of
   * userinfo): an active holder of `master.support.approve` in a session that
   * is nobody else's work. Drives the approval dialog and the admins' banner.
   */
  supportApprover?: boolean;
}

const _user = ref<ShellUser | null>(null);
const _userLoading = ref(false);

/**
 * Reactive view on the logged-in user, hydrated by `fetchUserInfo()`.
 * Stays `null` until the first successful `/oauth/userinfo` call.
 */
export function useShellUser(): { user: Ref<ShellUser | null>; loading: Ref<boolean> } {
  return { user: _user, loading: _userLoading };
}

/**
 * Seeds the reactive user pill from the persisted session so a reload while
 * logged in shows the name/email immediately, instead of flashing blank until
 * `fetchUserInfo()` resolves. fetchUserInfo() later replaces this with the
 * authoritative `/oauth/userinfo` record. No-op when logged out or already set.
 */
export function seedUserFromStorage(): void {
  if (_user.value) return;
  const session = readStoredSession();
  const principal = (session?.account ?? session?.user) as
    | { id?: unknown; email?: unknown; name?: unknown }
    | undefined;
  const email = typeof principal?.email === 'string' ? principal.email : '';
  if (!email) return;
  _user.value = {
    id: typeof principal?.id === 'number' ? principal.id : 0,
    name: typeof principal?.name === 'string' ? principal.name : '',
    email,
    accountId: null,
    accountUuid: null,
    support: readStoredSupport(),
  };
  scheduleSupportEnd(_user.value.support?.endsAt ?? null);
}

/**
 * Single in-flight refresh promise so concurrent 401s from multiple sub-apps
 * all wait on the same network call. Without this, n parallel 401s would each
 * spend the refresh token and only the first would succeed.
 */
let _refreshInFlight: Promise<boolean> | null = null;

/** When this browser last traded the refresh token, for the other windows. */
const REFRESHED_AT_KEY = 'blp_token_refreshed_at';
/** A 401 this soon after a refresh comes from the old token: the new one is there. */
const JUST_REFRESHED_MS = 10_000;

/**
 * The last trade got no answer (master unreachable, this page going away). That
 * says nothing about the credentials, and an unreachable master must never cost
 * the user their session: the next request asks again.
 */
let refreshUnanswered = false;

async function performRefresh(): Promise<boolean> {
  refreshUnanswered = false;
  // An impersonation session has no refresh token and a hard end: nothing to
  // trade, so report failure without a round trip. The caller's failure path
  // (handleAuthFailure) then returns to the impersonator.
  if (isImpersonating()) return false;
  const refresh = localStorage.getItem('blp_refresh_token');
  if (!refresh) return false;
  const generation = _sessionGeneration;
  // The login marker is shared with the other tabs (it lives next to the
  // credentials): a tab that replaced the session meanwhile changed it, which
  // the per-tab generation cannot see.
  loginId(); // a login from before the marker gets one now
  const marker = readRaw(LOGIN_ID_STORAGE_KEY);
  const replaced = () => generation !== _sessionGeneration || readRaw(LOGIN_ID_STORAGE_KEY) !== marker;
  // A refresh token works once, and every tile is a window of its own with the same
  // token: the second window to try would get a 401 and sign everybody out. So one
  // window at a time, and whoever waited finds the new token (I-3641). The lock does
  // not exist on a plain-HTTP host; `tradeRefreshToken` copes without.
  const locks = typeof navigator === 'undefined' ? undefined : navigator.locks;
  return locks
    ? locks.request('formica.oauth.refresh', () => tradeRefreshToken(refresh, replaced))
    : tradeRefreshToken(refresh, replaced);
}

async function tradeRefreshToken(seen: string, replaced: () => boolean): Promise<boolean> {
  const refresh = localStorage.getItem('blp_refresh_token');
  if (!refresh) return replaced();
  if (refresh !== seen) return true;
  const refreshedAt = Number(localStorage.getItem(REFRESHED_AT_KEY)) || 0;
  if (Date.now() - refreshedAt < JUST_REFRESHED_MS) return true;
  try {
    const res = await fetch(`${apiBase()}/oauth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...localeHeader() },
      body: JSON.stringify({ refresh_token: refresh, client_id: OAUTH_CLIENT_ID }),
    });
    // The session was replaced while the refresh was on its way: this answer
    // (a new pair, or a refusal) belongs to the one that is gone and is never
    // written or acted on. The caller's credentials are the current session's,
    // so it retries with those — reporting failure would sign the new session out.
    if (replaced()) return true;
    if (!res.ok) return await tradedElsewhere(refresh);
    const data = (await res.json()) as {
      access_token?: string;
      refresh_token?: string;
    };
    if (!data.access_token) return false;
    if (replaced()) return true;
    safeSet(REFRESHED_AT_KEY, String(Date.now()));
    // persist() rewrites every per-app token slot so sub-apps see the new
    // bearer on their next request. User/account info isn't returned by
    // refresh, so we leave whatever is already cached.
    persist({
      accessToken: data.access_token,
      refreshToken: data.refresh_token ?? refresh,
    });
    return true;
  } catch {
    // A failure of a refresh begun under a session that was replaced meanwhile
    // says nothing about the current one: retry with its credentials, never
    // report an auth failure that would sign it out.
    if (replaced()) return true;
    refreshUnanswered = true;
    return false;
  }
}

/**
 * The server refused the token. Without the lock another window may have spent it
 * a moment ago; a real refusal is none, and its token stays what it was.
 */
async function tradedElsewhere(refresh: string): Promise<boolean> {
  const manyWindows =
    typeof window !== 'undefined' && (window.parent !== window || window.frames.length > 0);
  if (manyWindows && !navigator.locks) {
    await new Promise((resolve) => setTimeout(resolve, 400));
  }
  return localStorage.getItem('blp_refresh_token') !== refresh;
}

/**
 * Trades the stored refresh token for a fresh access/refresh pair via
 * `/oauth/refresh` and persists the result. Concurrent calls share one
 * in-flight request. Returns `true` if a new access token is now in
 * localStorage, `false` otherwise (no refresh token / network error /
 * server rejected it).
 */
export function tryRefreshAccessToken(): Promise<boolean> {
  if (!_refreshInFlight) {
    _refreshInFlight = performRefresh().finally(() => {
      _refreshInFlight = null;
    });
  }
  return _refreshInFlight;
}

function isOnLoginRoute(): boolean {
  if (typeof window === 'undefined') return false;
  const hash = window.location.hash || '';
  return hash === '#/login' || hash.startsWith('#/login?') || hash.startsWith('#/login/');
}

// --- Before-sign-out hook -----------------------------------------------------
//
// Hosted apps hold things that belong to the signed-in account and must not
// outlive it (the edge app's remote support secrets, in-memory keys, drafts).
// `window.formica.onBeforeSignOut(cb)` lets an app drop them. The hook runs on
// every path that ends or replaces the session — never after the session is
// gone, so the app can still use it — and the shell does not wait for it.

/** Why the session is about to end or change hands. */
export type BeforeSignOutReason =
  | 'sign_out'
  | 'sign_out_all_devices'
  | 'session_lost'
  | 'account_switch';

export interface BeforeSignOutEvent {
  reason: BeforeSignOutReason;
}

export type BeforeSignOutHook = (event: BeforeSignOutEvent) => void | Promise<void>;

const beforeSignOutHooks = new Set<BeforeSignOutHook>();

/**
 * Registers a hook that runs just before the shell ends or replaces the
 * session: a sign-out (`sign_out`, `sign_out_all_devices`, which includes the
 * support operator's „Beenden"), a lost session (`session_lost`: refused and not
 * refreshable, dead at start-up, ended by master) and a change of account
 * (`account_switch`: an impersonation starts or ends). Returns the unsubscribe.
 *
 * The hook is called synchronously, while the credentials are still in place;
 * do the work that must happen before the wipe in that synchronous part — a
 * returned promise is not awaited, and the sign-out and the reload do not wait
 * for it. A hook that throws is ignored. Not called for a company switch (same
 * account), and a page reload alone is no sign-out.
 */
export function onBeforeSignOut(hook: BeforeSignOutHook): () => void {
  beforeSignOutHooks.add(hook);
  return () => {
    beforeSignOutHooks.delete(hook);
  };
}

function dropPendingAdoption(): void {
  try {
    sessionStorage.removeItem(PENDING_ADOPTION_KEY);
  } catch {
    /* no session storage */
  }
}

function runBeforeSignOut(reason: BeforeSignOutReason): void {
  // Whatever ends or replaces the session also ends a token staged from it.
  dropPendingAdoption();
  for (const hook of [...beforeSignOutHooks]) {
    try {
      void Promise.resolve(hook({ reason })).catch(() => undefined);
    } catch {
      /* a failing hook never keeps the sign-out from happening */
    }
  }
}

/**
 * Final fallback when refresh is impossible (no refresh token) or itself
 * fails: wipe local auth state and bounce to the login screen. A full
 * reload is required so the shared NxSocket drops + every sub-app store
 * re-reads localStorage as empty on its next mount.
 */
export function handleAuthFailure(): void {
  if (refreshUnanswered) return;
  // A dead impersonation session (expired, ended by master) returns to the
  // impersonator instead of logging them out.
  if (hasImpersonationKey()) {
    void endImpersonation();
    return;
  }
  runBeforeSignOut('session_lost');
  clearAuth();
  if (isOnLoginRoute()) return;
  window.location.hash = '#/login';
  window.location.reload();
}

/**
 * Explicit user-initiated sign-out. Exposed to sub-apps via
 * `window.formica.signOut` so module logout flows route through the shell.
 *
 * One server call: `POST /v1/session/signout` with the bearer (body
 * `{"allDevices": true}` for every session of the account). Master deletes the
 * session and revokes every token bound to it — including the access token
 * issued before the last refresh, which the client can no longer name — so no
 * separate `devices/signout_all` or `oauth/revoke` round trip is needed.
 *
 * Best-effort: a server that cannot be reached, or one that already rejects
 * the bearer (401), must not strand the user in a half-logged-in shell, so the
 * local wipe + redirect happens in every case.
 */
export async function signOut(allDevices = false): Promise<void> {
  // Signing out of an impersonation session ends only that session and
  // returns to the impersonator — never their own sign-in, and never every
  // device (master refuses `allDevices` for an impersonation bearer).
  if (hasImpersonationKey()) return endImpersonation();
  runBeforeSignOut(allDevices ? 'sign_out_all_devices' : 'sign_out');
  await postSignOut(allDevices);
  clearAuth();
  window.location.hash = '#/login';
  window.location.reload();
}

/** `POST /v1/session/signout` with the current bearer. Best-effort, never throws. */
async function postSignOut(allDevices: boolean): Promise<void> {
  if (!readBearer()) return;
  try {
    await fetch(`${apiBase()}/session/signout`, {
      method: 'POST',
      headers: buildAuthHeaders(allDevices ? { 'Content-Type': 'application/json' } : {}),
      ...(allDevices ? { body: JSON.stringify({ allDevices: true }) } : {}),
    });
  } catch {
    // Best-effort — the local sign-out still has to happen.
  }
}

// --- Impersonation (I-2327) --------------------------------------------------
//
// A superadmin opens a session as another account from the admin app. The
// admin app asks master for the token (`POST /v1/accounts/:id/impersonate` on
// the admin door) and hands it to the shell (`window.formica.startImpersonation`,
// `startShellImpersonation` in @blpde/connect). The shell keeps the
// impersonator's credentials under IMPERSONATION_STORAGE_KEY, runs on the
// impersonation bearer (access token only: no refresh, hard end) and, on the
// way back, ends that session on master and restores them.
//
// Every tab follows: the key appearing or vanishing reloads the other tabs. It
// is written after the bearer on the way in and removed after the bearer was
// restored on the way out, so a tab that reloads on it reads the new bearer.

/** What `startImpersonation` needs from the admin app. Never a URL parameter. */
export type ImpersonationStart = ShellImpersonationStart;

/** The impersonator's saved state (IMPERSONATION_STORAGE_KEY). */
interface SavedImpersonator {
  version: 1;
  accessToken: string;
  refreshToken: string | null;
  /** The raw `formica.oauth.session` blob, restored byte for byte. */
  session: string | null;
  returnTo: string;
  accountID: number | null;
  expiresAt: string | null;
}

/** A shell route path: `/…`, never a protocol-relative `//host` or a URL. */
function isRoutePath(value: unknown): value is string {
  return typeof value === 'string' && value.startsWith('/') && !value.startsWith('//');
}

function hasImpersonationKey(): boolean {
  try {
    return localStorage.getItem(IMPERSONATION_STORAGE_KEY) != null;
  } catch {
    return false;
  }
}

/** The saved impersonator, or null when absent or unreadable. */
function readSavedImpersonator(): SavedImpersonator | null {
  let raw: string | null;
  try {
    raw = localStorage.getItem(IMPERSONATION_STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const d = JSON.parse(raw) as Partial<SavedImpersonator> | null;
    if (!d || typeof d !== 'object' || typeof d.accessToken !== 'string' || !d.accessToken) return null;
    return {
      version: 1,
      accessToken: d.accessToken,
      refreshToken: typeof d.refreshToken === 'string' && d.refreshToken ? d.refreshToken : null,
      session: typeof d.session === 'string' ? d.session : null,
      returnTo: isRoutePath(d.returnTo) ? d.returnTo : '/',
      accountID: typeof d.accountID === 'number' ? d.accountID : null,
      expiresAt: typeof d.expiresAt === 'string' ? d.expiresAt : null,
    };
  } catch {
    return null;
  }
}

/** Whether this browser runs an impersonation session with a usable saved impersonator. */
export function isImpersonating(): boolean {
  return readSavedImpersonator() != null;
}

/** Writes a bearer set without persist(): no identity carry-over, no last email. */
function installCredentials(accessToken: string, refreshToken: string | null, session: string | null): void {
  startLogin();
  if (refreshToken) safeSet('blp_refresh_token', refreshToken);
  else safeRemove('blp_refresh_token');
  if (session) safeSet(SHARED_OAUTH_SESSION_KEY, session);
  else safeRemove(SHARED_OAUTH_SESSION_KEY);
  safeSet('blp_token', accessToken);
  for (const app of MIRRORED_SUBAPPS) safeSet(`${app}_token`, accessToken);
  resetStoredSessionValidation();
  if (typeof window !== 'undefined') window.dispatchEvent(new Event('formica:auth-changed'));
}

function readRaw(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

/** Puts the saved impersonator's credentials back and forgets the saved key. */
function restoreImpersonator(saved: SavedImpersonator): void {
  installCredentials(saved.accessToken, saved.refreshToken, saved.session);
  // Last: other tabs reload on this key and must find the restored bearer.
  safeRemove(IMPERSONATION_STORAGE_KEY);
  _user.value = null;
  cancelImpersonationTimer();
}

/**
 * Drops the caches keyed by the previous account, as the router's `?token=`
 * capture does. The reload that follows drops them too; this keeps the tab
 * clean in the moment before it.
 */
async function resetAccountCaches(): Promise<void> {
  await Promise.allSettled([
    import('@/applications').then((m) => m.resetAccountApplications()),
    import('@erp/connect/shell').then((m) => m.usePermissionStore().clear()),
  ]);
}

/**
 * Starts an impersonation session with the token master issued for it: saves
 * the impersonator's credentials under IMPERSONATION_STORAGE_KEY, installs the
 * impersonation bearer (no refresh token) and reloads into the shell home.
 * Every other tab follows. Throws (and changes nothing) without a token or a
 * signed-in impersonator, inside an impersonation session, or when the browser
 * cannot keep the saved credentials.
 */
export async function startImpersonation(start: ImpersonationStart): Promise<void> {
  const accessToken = typeof start?.accessToken === 'string' ? start.accessToken : '';
  if (!accessToken) throw new Error('impersonation_token_missing');
  if (hasImpersonationKey()) throw new Error('impersonation_active');
  const current = readRaw('blp_token');
  if (!current) throw new Error('impersonation_requires_session');
  runBeforeSignOut('account_switch');
  const saved: SavedImpersonator = {
    version: 1,
    accessToken: current,
    refreshToken: readRaw('blp_refresh_token'),
    session: readRaw(SHARED_OAUTH_SESSION_KEY),
    returnTo: isRoutePath(start.returnTo) ? start.returnTo : '/',
    accountID: typeof start.accountID === 'number' ? start.accountID : null,
    expiresAt: typeof start.expiresAt === 'string' ? start.expiresAt : null,
  };

  // The blob sub-apps read their identity from names the impersonated account,
  // never the impersonator.
  const account = start.account ?? (saved.accountID != null ? { id: saved.accountID } : null);
  const session: Record<string, unknown> = {
    access_token: accessToken,
    accessToken,
    apiBaseUrl: `${getApiOrigin()}/v1`,
  };
  if (account) session.account = account;
  installCredentials(accessToken, null, JSON.stringify(session));

  // Last, and checked: without the saved key there is no way back, so a
  // browser that cannot keep it gets the impersonator's credentials back.
  safeSet(IMPERSONATION_STORAGE_KEY, JSON.stringify(saved));
  if (readRaw(IMPERSONATION_STORAGE_KEY) == null) {
    installCredentials(saved.accessToken, saved.refreshToken, saved.session);
    throw new Error('impersonation_storage_unavailable');
  }

  _user.value = null;
  await resetAccountCaches();
  window.location.hash = '#/';
  window.location.reload();
}

let _endInFlight: Promise<void> | null = null;

/**
 * Ends the impersonation session and returns to the impersonator: signs the
 * impersonation bearer out on master (best-effort — an expired one answers
 * 401), restores the saved credentials and reloads into the saved admin route.
 * A missing or unreadable saved key leaves nothing to return to: then it is a
 * plain sign-out to the login view. Concurrent calls share one run.
 */
export function endImpersonation(): Promise<void> {
  if (!_endInFlight) {
    _endInFlight = runEndImpersonation().finally(() => {
      _endInFlight = null;
    });
  }
  return _endInFlight;
}

async function runEndImpersonation(): Promise<void> {
  const saved = readSavedImpersonator();
  runBeforeSignOut(saved ? 'account_switch' : 'sign_out');
  await postSignOut(false);
  if (!saved) {
    clearAuth();
    window.location.hash = '#/login';
    window.location.reload();
    return;
  }
  restoreImpersonator(saved);
  await resetAccountCaches();
  window.location.hash = `#${saved.returnTo}`;
  window.location.reload();
}

// Master ends an impersonation session at `impersonation_expires_at` however
// active it is; return to the impersonator right then instead of waiting for
// the first refused request.
let _impersonationTimer: ReturnType<typeof setTimeout> | null = null;

function cancelImpersonationTimer(): void {
  if (_impersonationTimer != null) clearTimeout(_impersonationTimer);
  _impersonationTimer = null;
}

function scheduleImpersonationEnd(expiresAt: string | null): void {
  cancelImpersonationTimer();
  if (!expiresAt || !hasImpersonationKey()) return;
  const end = Date.parse(expiresAt);
  if (!Number.isFinite(end)) return;
  _impersonationTimer = setTimeout(() => {
    _impersonationTimer = null;
    void endImpersonation();
  }, Math.max(0, end - Date.now()));
}

/** The impersonation block of a `/oauth/userinfo` payload, or null. */
function parseImpersonation(d: Record<string, unknown>): ShellImpersonation | null {
  const raw = d.impersonator;
  if (!raw || typeof raw !== 'object') return null;
  const p = raw as Record<string, unknown>;
  const id = p.account_id ?? p.accountID;
  const uuid = p.account_uuid ?? p.accountUUID;
  const expiresAt = d.impersonation_expires_at ?? d.impersonationExpiresAt;
  return {
    impersonator: {
      accountId: id != null && Number.isFinite(Number(id)) ? Number(id) : null,
      accountUuid: typeof uuid === 'string' ? uuid : null,
      name: typeof p.name === 'string' ? p.name : '',
      email: typeof p.email === 'string' ? p.email : '',
    },
    expiresAt: typeof expiresAt === 'string' && expiresAt ? expiresAt : null,
  };
}

// Master ends a remote support session at the time the admin approved,
// however active it is (and removes its token): leave right then instead of
// waiting for the first refused request. There is nobody to return to: the
// session ends in the sign-in view.
let _supportTimer: ReturnType<typeof setTimeout> | null = null;

function cancelSupportTimer(): void {
  if (_supportTimer != null) clearTimeout(_supportTimer);
  _supportTimer = null;
}

function scheduleSupportEnd(endsAt: string | null): void {
  cancelSupportTimer();
  if (!endsAt) return;
  const end = Date.parse(endsAt);
  if (!Number.isFinite(end)) return;
  // Bound to the login it was set for: if another tab signed in meanwhile (the
  // shared marker changed), this end is not the current login's.
  const marker = readRaw(LOGIN_ID_STORAGE_KEY);
  _supportTimer = setTimeout(
    () => {
      _supportTimer = null;
      if (readRaw(LOGIN_ID_STORAGE_KEY) !== marker) return;
      handleAuthFailure();
    },
    Math.max(0, end - Date.now()),
  );
}

/** The `support` block of a `/oauth/userinfo` payload, or null. */
function parseSupport(d: Record<string, unknown>): ShellSupport | null {
  const raw = d.support;
  if (!raw || typeof raw !== 'object') return null;
  const p = raw as Record<string, unknown>;
  const operator = p.operator_name ?? p.operatorName ?? p.operator;
  const name =
    typeof operator === 'string'
      ? operator
      : typeof (operator as { name?: unknown } | null | undefined)?.name === 'string'
        ? (operator as { name: string }).name
        : '';
  const id = p.request_id ?? p.requestID ?? p.requestId ?? p.id;
  const endsAt = p.ends_at ?? p.endsAt;
  return {
    operatorName: name,
    requestId: typeof id === 'string' ? id : '',
    endsAt: typeof endsAt === 'string' && endsAt ? endsAt : null,
  };
}

if (typeof window !== 'undefined') {
  // Sub-apps signal 401 with `window.dispatchEvent(new CustomEvent(
  // 'formica:auth-error'))`. We try refresh first; on success the sub-app's
  // next request rides the new bearer and we publish a recovered event so
  // it can retry the failed call. On failure we kick to /login.
  window.addEventListener('formica:auth-error', async () => {
    const ok = await tryRefreshAccessToken();
    if (ok) {
      window.dispatchEvent(new CustomEvent('formica:auth-recovered'));
    } else {
      handleAuthFailure();
    }
  });
  // Cross-tab auth sync. `storage` fires only in OTHER tabs, so this reacts to
  // login/logout/refresh that happened elsewhere:
  //   token removed  → logged out elsewhere → drop our session + go to login
  //   token appeared → logged in elsewhere   → reload to pick up the session
  //   token rotated  → refreshed elsewhere   → nudge same-page stores to re-read
  //   impersonation started or ended elsewhere → reload into the new account
  //   (the key is written last on the way in and removed last on the way out,
  //   so the bearer it goes with is already in place)
  window.addEventListener('storage', (event) => {
    if (event.key === IMPERSONATION_STORAGE_KEY) {
      if (!event.oldValue !== !event.newValue) window.location.reload();
      return;
    }
    if (event.key !== 'blp_token') return;
    if (!event.newValue) {
      refreshUnanswered = false;
      // Another tab removes the token on its way to a new one (it replaces the
      // session: remove, then write) and this event arrives after the write.
      // Reconcile against what storage holds now: credentials there are the new
      // login's — adopt them with a reload, never wipe them. Only storage that
      // really holds no credentials is a sign-out.
      if (hasAuthCredentials()) window.location.reload();
      else handleAuthFailure();
    } else if (!event.oldValue) {
      window.location.reload();
    } else if (
      _stateMarker &&
      readRaw(LOGIN_ID_STORAGE_KEY) &&
      readRaw(LOGIN_ID_STORAGE_KEY) !== _stateMarker
    ) {
      // Another tab signed in (a different login, not a refresh): what this tab
      // loaded — the user, the support metadata and its timers — belongs to the
      // old one. Drop the timers and start over with the new login.
      cancelSupportTimer();
      cancelImpersonationTimer();
      window.location.reload();
    } else {
      window.dispatchEvent(new Event('formica:auth-changed'));
    }
  });
  // Expose the refresh + sign-out helpers to sub-apps that await the refresh
  // inline (see @blpde/connect's fetchWithAuth) instead of the event dance.
  // Merge rather than clobber so a dev hot-reload can't strip a method a
  // sub-app already captured.
  // `startImpersonation` / `endImpersonation` are the admin app's way into an
  // impersonation session and back (`startShellImpersonation` in @blpde/connect).
  const bridged = window as Window & {
    formica?: {
      tryRefresh?: () => Promise<boolean>;
      signOut?: (allDevices?: boolean) => Promise<void>;
      startImpersonation?: (start: ImpersonationStart) => Promise<void>;
      endImpersonation?: () => Promise<void>;
      onBeforeSignOut?: (hook: BeforeSignOutHook) => () => void;
      loginId?: () => string;
    };
  };
  bridged.formica = {
    ...bridged.formica,
    tryRefresh: tryRefreshAccessToken,
    signOut,
    startImpersonation,
    endImpersonation,
    onBeforeSignOut,
    loginId,
  };
  // Populate the user pill from the persisted session on first load so a reload
  // while logged in doesn't flash a blank pill until /oauth/userinfo returns.
  seedUserFromStorage();
}

/**
 * Pulls `/oauth/userinfo` with the current bearer and populates the shared
 * `useShellUser()` state. On 401 it tries `tryRefreshAccessToken()` once and
 * replays the request with the new token; if refresh also fails it calls
 * `handleAuthFailure()` (clearAuth + redirect to /login).
 */
export async function fetchUserInfo(): Promise<ShellUser | null> {
  return (await loadUserInfo(true, true)).user;
}

/**
 * Outcome of a `/oauth/userinfo` round trip. `rejected` separates "the server
 * says these credentials are dead" from "we could not ask" — an unreachable
 * master must never cost the user their session.
 */
interface UserInfoOutcome {
  user: ShellUser | null;
  rejected: boolean;
}

async function loadUserInfo(canRefresh: boolean, redirectOnReject: boolean): Promise<UserInfoOutcome> {
  const bearer = readBearer();
  if (!hasAuthCredentials()) {
    _user.value = null;
    return { user: null, rejected: false };
  }
  /** The credentials are dead: forget the user and, for runtime callers, bounce. */
  const rejected = (): UserInfoOutcome => {
    _user.value = null;
    if (redirectOnReject) handleAuthFailure();
    return { user: null, rejected: true };
  };
  /** We could not ask. Says nothing about the credentials — keep them. */
  const unanswered = (): UserInfoOutcome => {
    seedUserFromStorage();
    return { user: _user.value, rejected: false };
  };
  // The session this question belongs to: an answer that arrives after the
  // session was replaced — here, or by another tab (the shared login marker) —
  // describes a session that is gone and must not touch the current one.
  const generation = _sessionGeneration;
  loginId();
  const marker = readRaw(LOGIN_ID_STORAGE_KEY);
  const stale = () => generation !== _sessionGeneration || readRaw(LOGIN_ID_STORAGE_KEY) !== marker;
  const superseded = (): UserInfoOutcome => ({ user: _user.value, rejected: false });
  _userLoading.value = true;
  try {
    const res = await fetch(`${apiBase()}/oauth/userinfo`, {
      headers: buildAuthHeaders(),
      // Without a deadline an unresponsive host holds the router's boot gate
      // until the TCP timeout, and the user cannot even reach the login form to
      // fix the address.
      signal: AbortSignal.timeout(USERINFO_TIMEOUT_MS),
    });
    if (stale()) return superseded();
    if (!res.ok) {
      const unauthorized = res.status === 401 || res.status === 403;
      if (unauthorized && bearer && canRefresh) {
        const refreshed = await tryRefreshAccessToken();
        if (refreshed) return loadUserInfo(false, redirectOnReject);
        if (refreshUnanswered) return unanswered();
      }
      // A 5xx or a proxy error is the server failing, not a verdict on us.
      return unauthorized ? rejected() : unanswered();
    }
    // Read the body as text first: a read that fails (the page is reloading and
    // aborts it after the headers, the connection drops) throws here and lands
    // in the outer catch as "could not ask", keeping the session. Only a body
    // that arrived in full and is not JSON is a verdict.
    const text = await res.text();
    if (stale()) return superseded();
    let d: Record<string, unknown>;
    try {
      d = JSON.parse(text) as Record<string, unknown>;
    } catch {
      // 200 with a non-JSON body — a captive portal or SSO gateway answering
      // for master. Not a session, however friendly the status code.
      return rejected();
    }
    const user: ShellUser = {
      id: Number(d.userID ?? d.user_id ?? d.sub ?? 0) || 0,
      name: typeof d.name === 'string' ? d.name : '',
      email: typeof d.email === 'string' ? d.email : '',
      accountId: d.accountID != null
        ? Number(d.accountID)
        : d.account_id != null
          ? Number(d.account_id)
          : null,
      accountUuid: typeof d.accountUUID === 'string'
        ? d.accountUUID
        : typeof d.account_uuid === 'string'
          ? d.account_uuid
          : null,
    };
    if (!user.id && !user.email) {
      // JSON, but nothing that identifies anyone — same story as above.
      return rejected();
    }
    user.impersonation = parseImpersonation(d);
    scheduleImpersonationEnd(user.impersonation?.expiresAt ?? null);
    user.support = parseSupport(d);
    if (user.support) safeSet(SUPPORT_STORAGE_KEY, JSON.stringify(d.support));
    else safeRemove(SUPPORT_STORAGE_KEY);
    user.supportApprover = d.support_approver === true;
    scheduleSupportEnd(user.support?.endsAt ?? null);
    _stateMarker = marker;
    _user.value = user;
    void loadShellConfigs();
    return { user, rejected: false };
  } catch {
    // Network / CORS / timeout — not a verdict on the credentials.
    return unanswered();
  } finally {
    _userLoading.value = false;
  }
}

/**
 * One-shot boot validation of whatever credentials are in localStorage.
 *
 * The router's auth gate only knows whether a token EXISTS, so a token that
 * died elsewhere (signed out in another app, session revoked) used to carry the
 * user into the shell, where the dashboard rendered empty until the first API
 * call bounced them back out (I-385). This asks master once per page load,
 * before any authenticated view mounts.
 *
 * `false` means "show the login view": either nothing is stored, or master
 * rejected it — in which case the stale state is dropped here. A master we
 * cannot reach yields `true`: offline is not a rejection, and the runtime 401
 * handling still catches a genuinely dead session later.
 */
export function ensureValidStoredSession(): Promise<boolean> {
  if (!hasAuthCredentials()) return Promise.resolve(false);
  if (!_storedSessionValidation) {
    _storedSessionValidation = loadUserInfo(true, false).then(({ rejected }) => {
      if (!rejected) return true;
      // An impersonation session that died while the browser was closed:
      // return to the impersonator and ask again for their credentials. A
      // missing or unreadable saved key leaves only the login view.
      const saved = readSavedImpersonator();
      runBeforeSignOut(saved ? 'account_switch' : 'session_lost');
      if (saved) {
        restoreImpersonator(saved);
        return ensureValidStoredSession();
      }
      clearAuth();
      return false;
    });
  }
  return _storedSessionValidation;
}

let _storedSessionValidation: Promise<boolean> | null = null;

/** Test seam: forget the one-shot result so the next call asks again. */
export function resetStoredSessionValidation(): void {
  _storedSessionValidation = null;
}

export interface SessionScope {
  tenantID: number;
  companyID: number;
}

export interface SessionScopeCompany {
  id: number;
  parentID: number;
  code: string;
  name: string;
}

export interface SessionScopeTenant {
  id: number;
  key: string;
  companies: SessionScopeCompany[];
}

/** GET /v1/session/scopes: the active scope and every pair the account may switch to. */
export interface SessionScopes extends SessionScope {
  tenants: SessionScopeTenant[];
}

function sessionSwitchError(res: Response, body: unknown): Error {
  const rec = body && typeof body === 'object' ? (body as Record<string, unknown>) : {};
  if (typeof rec.error === 'string' && rec.error) return new Error(rec.error);
  return new Error(`switch failed (${res.status})`);
}

function toNumber(value: unknown): number {
  return Number(value ?? 0) || 0;
}

/**
 * The tenants and companies the account may switch to, next to the session's
 * active scope (master applies the same gates as the switch). null without
 * credentials or on any failure.
 */
export async function fetchSessionScopes(): Promise<SessionScopes | null> {
  if (!hasAuthCredentials()) return null;
  try {
    const res = await fetch(`${apiBase()}/session/scopes`, { headers: buildAuthHeaders() });
    if (!res.ok) return null;
    const d = (await res.json()) as Record<string, unknown>;
    const tenants = Array.isArray(d.tenants) ? (d.tenants as Record<string, unknown>[]) : [];
    return {
      tenantID: toNumber(d.tenantID),
      companyID: toNumber(d.companyID),
      tenants: tenants.map((t) => ({
        id: toNumber(t.id),
        key: typeof t.key === 'string' ? t.key : '',
        companies: (Array.isArray(t.companies) ? (t.companies as Record<string, unknown>[]) : []).map((c) => ({
          id: toNumber(c.id),
          parentID: toNumber(c.parentID),
          code: typeof c.code === 'string' ? c.code : '',
          name: typeof c.name === 'string' ? c.name : '',
        })),
      })),
    };
  } catch {
    return null;
  }
}

/**
 * Moves the session to a tenant and company in one step (POST /v1/session/scope).
 * Every token of the session follows, in every tab. Master's contract is one
 * OAuth refresh after the switch; this does it. The caller reloads the shell.
 */
export async function switchSessionScope(tenantID: number, companyID: number): Promise<SessionScope> {
  // Accounts, their shares and the Mail and Calendar caches belong to the
  // tenant: only a move to another tenant ends the account selection.
  const leavesTenant = invalidateAccountSelectionForTenant(tenantID);
  try {
    const res = await fetch(`${apiBase()}/session/scope`, {
      method: 'POST',
      headers: buildAuthHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ tenantID, companyID }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw sessionSwitchError(res, body);
    await tryRefreshAccessToken();
    const rec = body as Record<string, unknown>;
    return { tenantID: toNumber(rec.tenantID) || tenantID, companyID: toNumber(rec.companyID) || companyID };
  } catch (error) {
    // A refused or lost response does not prove which tenant the server kept.
    // Recover from authoritative discovery, never revive old delegated grants.
    if (leavesTenant) await refreshAccountSelection();
    throw error;
  }
}
